import { groupBy, map } from 'lodash';
import React from 'react';
import {
  Keyboard,
  ListRenderItem,
  TextInput as NativeTextInput,
  SectionListRenderItem,
  StyleSheet,
  View,
} from 'react-native';

import { UserAvatar } from '@components/Avatar';
import Chip from '@components/Chip';
import Composer from '@components/Composer';
import Divider from '@components/Divider';
import FlatList from '@components/FlatList';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import SectionList from '@components/SectionList';
import { Caption, Paragraph } from '@components/Typography';
import { LargeUserCardList } from '@components/User/CardList';
import { Row } from '@components/View';
import { useChats } from '@context/ChatsContext';
import { useAppTheme } from '@context/ThemeContext';
import { listenToChat, listenToMessages, updateChatRead } from '@db/Chats';
import { useAddChatMessageMutation, useAddOrOpenChatWithMessageMutation } from '@db/Chats/Queries';
import { useUserSearch } from '@db/Search';
import { errorAlert } from '@helpers/Alert';
import { dateToString } from '@helpers/Dates';
import { filterChatUserInfo } from '@helpers/Formatters';
import { logError } from '@helpers/Loggers';
import { uniqueBy } from '@helpers/Parsers';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { createAppNavigation } from '@navigation/AppNavigation';
import { CommonRoute } from '@navigation/Routes';
import { ChatScreenProps, CreateChatScreenProps } from '@navigation/Types';
import { User, UserInfo, UserSearchResult } from '@types';
import { Chat, Message } from '@types';

const CHIP_AVATAR_SIZE = 24;
const MESSAGE_AVATAR_SIZE = 30;
const RECIPIENT_HEADER_MIN_HEIGHT = 56;
const RECIPIENT_HEADER_ROW_MIN_HEIGHT = 44;
const RECIPIENT_INPUT_MIN_WIDTH = 120;
const NUM_MINUTES_BETWEEN_AVATARS = 2;

type MessagesSection = {
  date: string;
  data: Message[];
};

type ChatRouteScreenProps = ChatScreenProps | CreateChatScreenProps;
type SelectableChatUser = User | UserSearchResult;

export default function ChatScreen({ navigation, route }: ChatRouteScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const isDraftChat = route.name === CommonRoute.CreateChat;
  const chatId = route.name === CommonRoute.Chat ? route.params.chatId : undefined;
  const currentUserId = getCurrentUserId();
  const { chats } = useChats();
  const appTheme = useAppTheme();
  const [chat, setChat] = React.useState<Chat | undefined>(undefined);
  const [loading, setLoading] = React.useState(!isDraftChat);
  const [sections, setSections] = React.useState<MessagesSection[]>([]);
  const [users, setUsers] = React.useState<UserInfo[]>([]);
  const [usersSelected, setUsersSelected] = React.useState<UserInfo[]>([]);
  const [searchText, setSearchText] = React.useState('');
  const [inputText, setInputText] = React.useState<string>('');
  const sendMessageMutation = useAddChatMessageMutation();
  const addOrOpenChatWithMessageMutation = useAddOrOpenChatWithMessageMutation({ chats });
  const sending = sendMessageMutation.isPending || addOrOpenChatWithMessageMutation.isPending;
  const selectedUserIds = React.useMemo(
    () => new Set(usersSelected.map((user) => user.userId)),
    [usersSelected]
  );
  const excludedSearchUserIds = React.useMemo(
    () => [currentUserId, ...usersSelected.map((user) => user.userId)],
    [currentUserId, usersSelected]
  );
  const { results: usersResult } = useUserSearch({
    query: isDraftChat ? searchText : '',
    excludedUserIds: excludedSearchUserIds,
  });
  const draftUsers = React.useMemo<SelectableChatUser[]>(() => {
    return searchText
      ? usersResult.filter(
          (user) => user.userId !== currentUserId && !selectedUserIds.has(user.userId)
        )
      : [];
  }, [currentUserId, searchText, selectedUserIds, usersResult]);
  const headerUsers = isDraftChat ? usersSelected : filterChatUserInfo(users, currentUserId);
  const screenTitle = isDraftChat ? 'New Message' : getChatScreenTitle(users, currentUserId);
  const messageText = inputText.trim();
  const sendDisabled =
    sending || messageText.length === 0 || (isDraftChat && usersSelected.length === 0);

  const markRead = React.useCallback(async () => {
    if (!chatId) return;

    try {
      await updateChatRead(chatId);
    } catch (error) {
      logError(error);
    }
  }, [chatId]);

  React.useEffect(() => {
    if (!chatId) {
      return undefined;
    }

    return listenToChat(
      chatId,
      (chat) => {
        setChat(chat);
        setUsers(chat.userDocs);
        setLoading(false);
      },
      (error) => logError(error)
    );
  }, [chatId]);

  React.useEffect(() => {
    if (!chatId) {
      return undefined;
    }

    return listenToMessages(
      chatId,
      (messages) => {
        const sections = map(
          groupBy(
            // need to filter for non-null createdAt due to
            // the way serverTimestamp works
            // ref: https://stackoverflow.com/a/47781432
            messages.filter((message) => message.createdAt),
            (message: Message) => {
              return dateToString(message.createdAt, 'PP');
            }
          ),
          (value, key) => ({
            date: key,
            data: value,
          })
        );
        setSections(sections);
      },
      (error) => logError(error)
    );
  }, [chatId]);

  React.useEffect(() => {
    if (chatId && (chat?.unreadMessageCount?.[currentUserId] ?? 0) > 0) {
      void markRead();
    }
  }, [chat?.unreadMessageCount, chatId, currentUserId, markRead]);

  React.useEffect(() => {
    navigation.setOptions({ title: screenTitle });
  }, [navigation, screenTitle]);

  const removeSelectedUser = React.useCallback((userId: string) => {
    setUsersSelected((selected) => selected.filter((user) => user.userId !== userId));
  }, []);

  const renderUserChip: ListRenderItem<UserInfo> = ({ item }) => {
    return (
      <Chip
        onClose={isDraftChat ? () => removeSelectedUser(item.userId) : undefined}
        onPress={isDraftChat ? undefined : () => appNavigation.openProfile(item.userId)}
        style={styles.userChip}
        avatar={
          <UserAvatar
            displayName={item.displayName}
            photoUrl={item.photoUrl}
            size={CHIP_AVATAR_SIZE}
          />
        }>
        {item.displayName}
      </Chip>
    );
  };

  const onSelectDraftUser = React.useCallback((user: SelectableChatUser) => {
    setUsersSelected((selected) => uniqueBy([...selected, toUserInfo(user)], 'userId'));
    setSearchText('');
  }, []);

  const onSend = async () => {
    if (sendDisabled) {
      return;
    }

    if (chatId) {
      const outcome = await sendMessageMutation.mutateAsync({ chatId, text: messageText });
      if (outcome.ok) {
        setInputText('');
      } else {
        errorAlert(outcome.error, { onAction: onSend });
      }
      return;
    }

    const outcome = await addOrOpenChatWithMessageMutation.mutateAsync({
      users: usersSelected,
      text: messageText,
    });
    if (outcome.ok) {
      setInputText('');
      navigation.replace(CommonRoute.Chat, { chatId: outcome.data });
    } else {
      errorAlert(outcome.error, { onAction: onSend });
    }
  };

  const renderItem: SectionListRenderItem<Message, MessagesSection> = ({
    item,
    index,
    section,
  }) => {
    const messageText = item.text.trim();

    if (!messageText) {
      return null;
    }

    const lastIndex = section.data.length - 1;
    // previous message is next message in list due to sort order
    const previousMessage = section.data[Math.min(index + 1, lastIndex)];
    const isSameUser = item.user.userId === previousMessage.user.userId;
    // first message is last in list due to sort order
    const isFirstMessage = index === lastIndex;
    const minutesSincePreviousMessage =
      (item.createdAt.getTime() - previousMessage.createdAt.getTime()) / 60000;
    const shouldRenderAvatar =
      !isSameUser || isFirstMessage || minutesSincePreviousMessage >= NUM_MINUTES_BETWEEN_AVATARS;

    return (
      <View style={styles.messageContainer}>
        {shouldRenderAvatar ? (
          <Row>
            <PressableOpacity
              style={styles.avatarContainer}
              onPress={() => appNavigation.openProfile(item.user.userId)}>
              <UserAvatar
                size={MESSAGE_AVATAR_SIZE}
                displayName={item.user.displayName}
                photoUrl={item.user.photoUrl}
              />
            </PressableOpacity>
            <View style={styles.messageTextContainer}>
              <Caption>
                {item.user.displayName} · {dateToString(item.createdAt, 'p')}
              </Caption>
              <Paragraph style={styles.messageText}>{messageText}</Paragraph>
            </View>
          </Row>
        ) : (
          <View style={[styles.messageTextContainer, styles.messageTextContainerWithoutUser]}>
            <Paragraph style={styles.messageText}>{messageText}</Paragraph>
          </View>
        )}
      </View>
    );
  };

  const userKeyExtractor = (item: UserInfo) => item.userId;
  const messageKeyExtractor = (item: Message) => item.messageId;

  const renderRecipientInput = () => (
    <NativeTextInput
      autoFocus={isDraftChat}
      autoCapitalize="words"
      autoCorrect={false}
      cursorColor={appTheme.colors.primary}
      onChangeText={(text) => setSearchText(text)}
      selectionColor={appTheme.colors.primary}
      style={[styles.recipientInput, { color: appTheme.colors.text }]}
      testID="screens.ChatScreen.TextInput.Recipients"
      value={searchText}
    />
  );

  const renderHeaderUserList = () => (
    <FlatList
      data={headerUsers}
      renderItem={renderUserChip}
      keyExtractor={userKeyExtractor}
      contentContainerStyle={styles.chipListContent}
      ListHeaderComponent={
        isDraftChat ? <Caption style={styles.recipientPrefix}>To:</Caption> : undefined
      }
      ListFooterComponent={isDraftChat ? renderRecipientInput() : undefined}
      ListFooterComponentStyle={isDraftChat ? styles.recipientInputFooter : undefined}
      testID="screens.ChatScreen.FlatList.HeaderUsers"
      horizontal
    />
  );

  const renderDraftUserList = () =>
    draftUsers.length > 0 ? (
      <LargeUserCardList
        data={draftUsers}
        itemProps={{ onPress: onSelectDraftUser }}
        testID="screens.ChatScreen.FlatList.Users"
        style={styles.messageList}
      />
    ) : (
      <View style={styles.messageList} />
    );

  const renderMessageList = () => (
    <SectionList
      inverted
      sections={sections}
      initialNumToRender={20}
      renderItem={renderItem}
      stickySectionHeadersEnabled={false}
      renderSectionFooter={({ section }) => (
        <Caption style={styles.sectionHeaderText}>{section.date}</Caption>
      )}
      keyExtractor={messageKeyExtractor}
      onTouchStart={Keyboard.dismiss}
      style={styles.messageList}
      testID="screens.ChatScreen.SectionList.Messages"
    />
  );

  return (
    <ScreenContainer loading={chatId ? loading : false}>
      <View style={styles.container}>
        <View style={styles.headerContainer} testID="screens.ChatScreen.View.Header">
          {renderHeaderUserList()}
          <Divider style={styles.divider} />
        </View>
        {isDraftChat ? renderDraftUserList() : renderMessageList()}
        <Divider />
        <Composer
          value={inputText}
          onChangeText={setInputText}
          onSend={onSend}
          disabled={sendDisabled}
          loading={sending}
          testID="screens.ChatScreen.View.Composer"
          inputTestID="screens.ChatScreen.TextInput.Message"
          sendTestID="screens.ChatScreen.IconButton.Send"
        />
      </View>
    </ScreenContainer>
  );
}

function toUserInfo(user: SelectableChatUser): UserInfo {
  return {
    userId: user.userId,
    displayName: user.displayName,
    photoUrl: user.photoUrl,
  };
}

function getChatScreenTitle(users: readonly UserInfo[], currentUserId: string) {
  const otherUserNames = users
    .filter((user) => user.userId !== currentUserId)
    .map((user) => user.displayName);

  return otherUserNames.length > 0 ? otherUserNames.join(', ') : 'Chat';
}

const styles = StyleSheet.create({
  avatarContainer: {
    alignSelf: 'flex-start',
    marginTop: 5,
  },
  chipListContent: {
    alignItems: 'center',
    flexGrow: 1,
    margin: 5,
    minHeight: RECIPIENT_HEADER_ROW_MIN_HEIGHT,
  },
  container: {
    flex: 1,
    width: '100%',
  },
  divider: {
    marginTop: 5,
  },
  headerContainer: {
    minHeight: RECIPIENT_HEADER_MIN_HEIGHT,
    paddingTop: 5,
  },
  messageContainer: {
    padding: 5,
  },
  messageList: {
    flex: 1,
  },
  messageText: {
    flex: 1,
  },
  messageTextContainer: {
    flex: 1,
    marginLeft: 5,
  },
  messageTextContainerWithoutUser: {
    marginLeft: MESSAGE_AVATAR_SIZE,
    paddingHorizontal: 5,
  },
  recipientInput: {
    flex: 1,
    minHeight: RECIPIENT_HEADER_ROW_MIN_HEIGHT,
    minWidth: RECIPIENT_INPUT_MIN_WIDTH,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  recipientInputFooter: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  recipientPrefix: {
    alignSelf: 'center',
    marginHorizontal: 5,
  },
  sectionHeaderText: {
    textAlign: 'center',
  },
  userChip: {
    marginHorizontal: 2,
  },
});
